import React from 'react';
import {Form} from 'react-bootstrap';
import {ChartType, selectableChartTypeNames} from '@/views/storyboard/utils/chartConstants';
import {BarOrientation} from 'entities/chart';
import i18n from 'i18n';

const chartTypeIconPaths = {
    bar: (
        <>
            <rect x="3" y="10" width="3.5" height="9" />
            <rect x="10" y="5" width="3.5" height="14" />
            <rect x="17" y="13" width="3.5" height="6" />
        </>
    ),
    hbar: (
        <>
            <rect x="3" y="3.5" width="9" height="3.5" />
            <rect x="3" y="9.25" width="16" height="3.5" />
            <rect x="3" y="15" width="6" height="3.5" />
        </>
    ),
    line: <polyline points="3,16 8,9 13,12 20,4" fill="none" />,
    area: <polyline points="3,16 8,9 13,12 20,4 20,19 3,19" />,
    pie: (
        <>
            <circle cx="11" cy="11" r="8" fill="none" />
            <path d="M11 11 L11 3 A8 8 0 0 1 19 11 Z" />
        </>
    ),
    sunburst: (
        <>
            <circle cx="11" cy="11" r="3.5" fill="none" />
            <path d="M11 11 L11 1.5 A9.5 9.5 0 0 1 20 11 Z" fill="none" />
        </>
    ),
    treemap: (
        <>
            <rect x="3" y="3" width="9" height="9" fill="none" />
            <rect x="13" y="3" width="6" height="5" fill="none" />
            <rect x="13" y="9" width="6" height="3" fill="none" />
            <rect x="3" y="13" width="16" height="6" fill="none" />
        </>
    ),
    table: (
        <>
            <rect x="3" y="4" width="16" height="14" fill="none" />
            <line x1="3" y1="9" x2="19" y2="9" />
            <line x1="11" y1="4" x2="11" y2="18" />
        </>
    ),
    pivot: (
        <>
            <rect x="3" y="4" width="16" height="14" fill="none" />
            <rect x="3" y="4" width="16" height="4" />
            <rect x="3" y="4" width="5" height="14" />
        </>
    ),
    wordcloud: (
        <>
            <text x="2" y="10" fontSize="9" stroke="none" fill="currentColor">Aa</text>
            <text x="11" y="18" fontSize="7" stroke="none" fill="currentColor">bc</text>
        </>
    ),
    textsamples: (
        <>
            <line x1="3" y1="6" x2="19" y2="6" />
            <line x1="3" y1="11" x2="19" y2="11" />
            <line x1="3" y1="16" x2="13" y2="16" />
        </>
    ),
    bignumber: (
        <text x="2" y="16" fontSize="12" stroke="none" fill="currentColor" fontWeight="600">12</text>
    ),
};

type ChartTypeIconKey = keyof typeof chartTypeIconPaths;

const chartTypeIconKeys: Partial<Record<ChartType, ChartTypeIconKey>> = {
    [ChartType.BarChart]: 'bar',
    [ChartType.LineChart]: 'line',
    [ChartType.AreaChart]: 'area',
    [ChartType.PieChart]: 'pie',
    [ChartType.SunburstChart]: 'sunburst',
    [ChartType.TreemapChart]: 'treemap',
    [ChartType.Table]: 'table',
    [ChartType.PivotTable]: 'pivot',
    [ChartType.Wordcloud]: 'wordcloud',
    [ChartType.TextSamples]: 'textsamples',
    [ChartType.BigNumber]: 'bignumber',
};

const chartTypeDisplayLabelKeys: Partial<Record<ChartType, string>> = {
    [ChartType.BarChart]: 'chart_settings_chart_type_bar.text',
    [ChartType.LineChart]: 'chart_settings_chart_type_line.text',
    [ChartType.AreaChart]: 'chart_settings_chart_type_area.text',
    [ChartType.PieChart]: 'chart_settings_chart_type_pie.text',
    [ChartType.SunburstChart]: 'chart_settings_chart_type_sunburst.text',
    [ChartType.TreemapChart]: 'chart_settings_chart_type_treemap.text',
    [ChartType.Table]: 'table.text',
    [ChartType.PivotTable]: 'chart_settings_chart_type_pivot.text',
    [ChartType.Wordcloud]: 'word_cloud.text',
    [ChartType.TextSamples]: 'text_samples.text',
    [ChartType.BigNumber]: 'chart_settings_chart_type_big_number.text',
};

const renderChartTypeIcon = (iconKey: ChartTypeIconKey) => (
    <svg
        className="chart-type-icon-selector__icon"
        width={22}
        height={22}
        viewBox="0 0 22 22"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {chartTypeIconPaths[iconKey]}
    </svg>
);

interface Props {
    value: ChartType;
    barOrientation: BarOrientation;
    onChange: (chartType: ChartType, barOrientation?: BarOrientation) => void;
    error?: string;
    touched?: boolean;
}

interface ChartTypeSelectorOption {
    type: ChartType;
    label: string;
    iconKey: ChartTypeIconKey;
    barOrientation?: BarOrientation;
}

const ChartTypeIconSelector: React.FC<Props> = ({value, barOrientation, onChange, error, touched}) => {
    const chartTypeOptions: ChartTypeSelectorOption[] = selectableChartTypeNames.flatMap(([type, label]) => {
        const chartType = type as ChartType;

        if (type === ChartType.BarChart) {
            return [
                {
                    type: chartType,
                    label: `${i18n.t('chart_settings_chart_type_bar.text')} ${i18n.t('chart_settings_bar_orientation_vertical.text')}`,
                    iconKey: 'bar' as ChartTypeIconKey,
                    barOrientation: BarOrientation.Vertical,
                },
                {
                    type: chartType,
                    label: `${i18n.t('chart_settings_chart_type_bar.text')} ${i18n.t('chart_settings_bar_orientation_horizontal.text')}`,
                    iconKey: 'hbar' as ChartTypeIconKey,
                    barOrientation: BarOrientation.Horizontal,
                },
            ];
        }

        const iconKey = chartTypeIconKeys[chartType];
        const displayLabelKey = chartTypeDisplayLabelKeys[chartType];

        if (!iconKey) {
            return [];
        }

        return [{
            type: chartType,
            label: displayLabelKey ? i18n.t(displayLabelKey) : label.replace(/\s+chart$/i, ''),
            iconKey,
        }];
    });

    return (
        <Form.Group className="cs-field chart-type-icon-selector" controlId="chartType" data-cy="chart-settings-form-chart-type">
            <label className="cs-field__label">
                {i18n.t('jt_chart_type.text')}
            </label>
            <div className="chart-type-icon-selector__grid">
                {chartTypeOptions.map(({type, label, iconKey, barOrientation: optionBarOrientation}) => {
                const selected = value === type
                    && (
                        type !== ChartType.BarChart
                        || optionBarOrientation === barOrientation
                    );

                return (
                    <button
                        key={`${type}-${optionBarOrientation || 'default'}`}
                        type="button"
                        className={`chart-type-icon-selector__btn chart-type-icon-selector__btn--${iconKey}${selected ? ' chart-type-icon-selector__btn--selected' : ''}`}
                        onClick={() => onChange(type, optionBarOrientation)}
                        aria-label={label}
                        aria-pressed={selected}
                    >
                        {renderChartTypeIcon(iconKey)}
                        <span className="chart-type-icon-selector__label">
                            {label}
                        </span>
                    </button>
                );
            })}
            </div>
            {touched && error ? (
                <div className="invalid-feedback d-block mt-1">{error}</div>
            ) : null}
        </Form.Group>
    );
};

export default ChartTypeIconSelector;
